import { AlertCircle, InfoIcon } from 'lucide-react'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../../../ui/tooltip'
import { Label } from '../../../ui/label'
import { Input } from '../../../ui/input'
import { DropdownSelect } from '../../../ui/dropdown-select'

export const PAYMENT_TOKEN_OPTIONS = [
  { value: 'USDC', label: 'USDC' },
  { value: 'USDT', label: 'USDT' },
  { value: 'DAI', label: 'DAI' },
  { value: 'USDe', label: 'USDe' },
]

interface BuybackProtectionSectionProps {
  localData: {
    enableBuybackProtection: boolean
    buybackPaymentToken?: string
    buybackPrice?: string
    fundingWallet?: string
    buybackStartDate?: string
    buybackEndDate?: string
  }
  updateField: (field: string, value: any) => void
  errors: Record<string, string>
  ensureUTCDefault: (fieldName: string) => void
  scheduleId?: string
}

export function BuybackProtectionSection({
  localData,
  updateField,
  errors,
  ensureUTCDefault,
  scheduleId,
}: BuybackProtectionSectionProps) {
  const getFieldKey = (field: string) =>
    scheduleId ? `${scheduleId}-${field}` : field

  const getError = (field: string) => errors[getFieldKey(field)]

  return (
    <div className="bg-neutral-50 rounded-lg p-4 space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <div className="flex items-center gap-0">
            <Label
              htmlFor={getFieldKey('buybackPaymentToken')}
              className="text-sm font-normal"
            >
              Payment Token
            </Label>
            <TooltipProvider delayDuration={150}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    type="button"
                    aria-label="Payment token info"
                    className="text-neutral-500 hover:text-neutral-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-neutral-400 rounded-full p-1"
                  >
                    <InfoIcon className="h-3 w-3" />
                  </button>
                </TooltipTrigger>
                <TooltipContent
                  side="top"
                  align="start"
                  sideOffset={8}
                  className="max-w-[320px] rounded-md bg-white px-3 py-2 text-xs text-neutral-900 shadow-lg border border-neutral-200"
                >
                  <p>
                    Payment token: Choose the stablecoin used for buyback
                    execution. The selected token will be used to purchase
                    tokens during the buyback period.
                  </p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
          <DropdownSelect
            id={getFieldKey('buybackPaymentToken')}
            value={localData.buybackPaymentToken || ''}
            onChange={(value) => updateField('buybackPaymentToken', value)}
            options={PAYMENT_TOKEN_OPTIONS}
            placeholder="Select a stablecoin"
          />
          {getError('buybackPaymentToken') && (
            <p className="text-xs text-status-error inline-flex items-center gap-1">
              <AlertCircle className="w-3 h-3" />
              {getError('buybackPaymentToken')}
            </p>
          )}
        </div>
        <div className="space-y-2">
          <div className="flex items-center gap-0">
            <Label
              htmlFor={getFieldKey('buybackPrice')}
              className="text-sm font-normal"
            >
              Price
            </Label>
            <TooltipProvider delayDuration={150}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    type="button"
                    aria-label="Buyback price info"
                    className="text-neutral-500 hover:text-neutral-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-neutral-400 rounded-full p-1"
                  >
                    <InfoIcon className="h-3 w-3" />
                  </button>
                </TooltipTrigger>
                <TooltipContent
                  side="top"
                  align="start"
                  sideOffset={8}
                  className="max-w-[320px] rounded-md bg-white px-3 py-2 text-xs text-neutral-900 shadow-lg border border-neutral-200"
                >
                  <p>
                    Buyback price: The fixed USD price used for token
                    repurchases during the buyback period. This price will apply
                    only within the defined buyback window and subject to the
                    terms outlined in the sale documentation.
                  </p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
          <div className="relative">
            <Input
              id={getFieldKey('buybackPrice')}
              type="text"
              value={localData.buybackPrice || ''}
              onChange={(e) => {
                const value = e.target.value
                if (/^\d*\.?\d*$/.test(value)) {
                  updateField('buybackPrice', value)
                }
              }}
              onKeyDown={(e) => {
                if (
                  /[0-9]/.test(e.key) ||
                  e.key === '.' ||
                  e.key === 'Backspace' ||
                  e.key === 'Delete' ||
                  e.key === 'ArrowLeft' ||
                  e.key === 'ArrowRight' ||
                  e.key === 'Tab'
                ) {
                  return
                }
                e.preventDefault()
              }}
              placeholder="e.g., 0.50"
              className="pr-8"
            />
            <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
              <span className="text-sm text-neutral-500">USD</span>
            </div>
          </div>
          {getError('buybackPrice') && (
            <p className="text-xs text-status-error inline-flex items-center gap-1">
              <AlertCircle className="w-3 h-3" />
              {getError('buybackPrice')}
            </p>
          )}
        </div>
      </div>
      <div className="border-t border-neutral-200 my-4"></div>
      <div className="space-y-2">
        <div className="flex items-center gap-0">
          <Label
            htmlFor={getFieldKey('fundingWallet')}
            className="text-sm font-normal"
          >
            Funding Wallet
          </Label>
          <TooltipProvider delayDuration={150}>
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  type="button"
                  aria-label="Funding wallet info"
                  className="text-neutral-500 hover:text-neutral-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-neutral-400 rounded-full p-1"
                >
                  <InfoIcon className="h-3 w-3" />
                </button>
              </TooltipTrigger>
              <TooltipContent
                side="top"
                align="start"
                sideOffset={8}
                className="max-w-[320px] rounded-md bg-white px-3 py-2 text-xs text-neutral-900 shadow-lg border border-neutral-200"
              >
                <p>
                  The wallet address authorized to deposit funds for this
                  buyback campaign.
                </p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
        <Input
          id={getFieldKey('fundingWallet')}
          value={localData.fundingWallet || ''}
          onChange={(e) => updateField('fundingWallet', e.target.value)}
          placeholder="Enter wallet address (0x...)"
        />
        {getError('fundingWallet') && (
          <p className="text-xs text-status-error inline-flex items-center gap-1">
            <AlertCircle className="w-3 h-3" />
            {getError('fundingWallet')}
          </p>
        )}
      </div>
      <div className="border-t border-neutral-200 my-4"></div>
      <div className="space-y-2">
        <Label className="text-sm font-normal">Duration of the Buyback</Label>
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label
              htmlFor={getFieldKey('buybackStartDate')}
              className="text-xs font-normal"
            >
              Start Date (UTC)
            </Label>
            <Input
              id={getFieldKey('buybackStartDate')}
              type="datetime-local"
              placeholder="YYYY-MM-DD HH:MM"
              value={localData.buybackStartDate || ''}
              onChange={(e) => updateField('buybackStartDate', e.target.value)}
              onFocus={() => ensureUTCDefault('buybackStartDate')}
            />
            {getError('buybackStartDate') && (
              <p className="text-xs text-status-error inline-flex items-center gap-1">
                <AlertCircle className="w-3 h-3" />
                {getError('buybackStartDate')}
              </p>
            )}
          </div>
          <div className="space-y-2">
            <Label
              htmlFor={getFieldKey('buybackEndDate')}
              className="text-xs font-normal"
            >
              End Date (UTC)
            </Label>
            <Input
              id={getFieldKey('buybackEndDate')}
              type="datetime-local"
              placeholder="YYYY-MM-DD HH:MM"
              value={localData.buybackEndDate || ''}
              onChange={(e) => updateField('buybackEndDate', e.target.value)}
              onFocus={() => ensureUTCDefault('buybackEndDate')}
            />
            {getError('buybackEndDate') && (
              <p className="text-xs text-status-error inline-flex items-center gap-1">
                <AlertCircle className="w-3 h-3" />
                {getError('buybackEndDate')}
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
